import type { EventTypeEntry } from "./customers";
import { HQ_EVENT_NAME, readHqRule } from "./hq-rule";
import type { WebhookEndpoint } from "./webhook-endpoints";

export interface MetaRoutingRow {
  eventType: string;
  count: number;
  /** true when this row exists only because a non-disabled webhook endpoint
   * defaults to it — no matching event has been received yet. */
  connected: boolean;
}

/**
 * TASK-114 — merges received event-type counts with the default event types
 * of non-disabled webhook endpoints, so the "Welche Events an Meta?" table
 * shows a connected data source (e.g. an Ablefy preset) before its first
 * event arrives. Received counts always win; disabled endpoints and
 * endpoints whose default type was already received contribute no row.
 */
export function buildMetaRoutingRows(
  received: EventTypeEntry[],
  endpoints: (Pick<WebhookEndpoint, "event_type_default" | "status"> & {
    mapping_json?: WebhookEndpoint["mapping_json"];
  })[],
): MetaRoutingRow[] {
  const rows = new Map<string, MetaRoutingRow>(
    received.map((r) => [r.eventType, { eventType: r.eventType, count: r.count, connected: false }]),
  );
  for (const e of endpoints) {
    if (e.status === "disabled" || rows.has(e.event_type_default)) continue;
    rows.set(e.event_type_default, { eventType: e.event_type_default, count: 0, connected: true });
  }
  // TASK-234: an active HQ rule makes the endpoint emit HQLead, so the row
  // exists (and can be ticked) before the first HQ lead arrives.
  for (const e of endpoints) {
    const hq = readHqRule(e.mapping_json?.hq_rule);
    if (e.status === "disabled" || !hq?.enabled || rows.has(HQ_EVENT_NAME)) continue;
    rows.set(HQ_EVENT_NAME, { eventType: HQ_EVENT_NAME, count: 0, connected: true });
  }
  return [...rows.values()];
}
